import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Users,
  CreditCard,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  Shield,
  Download,
  Search,
  Calendar,
  Building2,
  User,
  Clock,
  ChevronRight,
  Plus,
  Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminFinancePortal - Owner/Admin Financial Control Center
 * 
 * Full financial transparency for admin users only.
 * Tabs: Overview, Subscriptions, Sponsorship, Ledger, Payouts, Audit
 */
const AdminFinancePortal = () => {
  const { user, authResolved } = useContext(AuthContext);
  // Auth token is stored in localStorage (not exposed via AuthContext).
  // Match the established pattern used by FounderConsole and FounderSponsorAdmin.
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Check admin access — wait for auth to resolve to avoid race redirect.
  useEffect(() => {
    if (!authResolved) return;
    if (!user) {
      navigate('/auth?mode=signin');
      return;
    }
    if (!user.is_admin) {
      toast.error("Admin access required");
      navigate('/');
      return;
    }
    setLoading(false);
  }, [user, authResolved, navigate]);
  
  if (loading || !user?.is_admin) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Financial Control Center</h1>
        </div>
        <p className="text-muted-foreground">
          Admin-only financial transparency and management
        </p>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6 mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="sponsorship">Sponsorship</TabsTrigger>
          <TabsTrigger value="ledger">Ledger</TabsTrigger>
          <TabsTrigger value="payouts">Payouts</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview">
          <OverviewTab token={token} />
        </TabsContent>
        
        <TabsContent value="subscriptions">
          <SubscriptionsTab token={token} />
        </TabsContent>
        
        <TabsContent value="sponsorship">
          <SponsorshipTab token={token} />
        </TabsContent>
        
        <TabsContent value="ledger">
          <LedgerTab token={token} />
        </TabsContent>
        
        <TabsContent value="payouts">
          <PayoutsTab token={token} />
        </TabsContent>
        
        <TabsContent value="audit">
          <AuditTab token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// --- A) OVERVIEW TAB ---
const OverviewTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchOverview();
  }, []);
  
  const fetchOverview = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/finance/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setData(await res.json());
      }
    } catch (error) {
      toast.error("Failed to load overview");
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  
  if (!data) {
    return <Alert><AlertDescription>Unable to load financial overview</AlertDescription></Alert>;
  }
  
  return (
    <div className="space-y-6">
      {/* Revenue Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <DollarSign className="h-4 w-4" />
              Gross MRR
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              ${data.revenue.gross_mrr.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Monthly recurring revenue</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <TrendingUp className="h-4 w-4" />
              Net MRR (Est.)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              ${data.revenue.estimated_net_mrr.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">After Stripe fees (~${data.revenue.stripe_fee_estimate.toFixed(2)})</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              Outstanding Liability
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">
              ${data.liability.total_accrued_pending.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Accrued to users (unpaid)</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Subscription Stats */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Active Subscriptions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Total Active</span>
                <Badge variant="outline" className="text-lg">{data.subscriptions.total_active}</Badge>
              </div>
              
              <div className="border-t pt-3">
                <p className="font-medium mb-2 flex items-center gap-1">
                  <Building2 className="h-4 w-4" /> Business ({data.subscriptions.business.total})
                </p>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="bg-gray-50 rounded p-2 text-center">
                    <div className="font-semibold">{data.subscriptions.business.by_tier.community_host}</div>
                    <div className="text-xs text-muted-foreground">Community</div>
                  </div>
                  <div className="bg-blue-50 rounded p-2 text-center">
                    <div className="font-semibold">{data.subscriptions.business.by_tier.local_featured}</div>
                    <div className="text-xs text-muted-foreground">Local</div>
                  </div>
                  <div className="bg-purple-50 rounded p-2 text-center">
                    <div className="font-semibold">{data.subscriptions.business.by_tier.market_sponsor}</div>
                    <div className="text-xs text-muted-foreground">Market</div>
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-3">
                <p className="font-medium mb-2 flex items-center gap-1">
                  <User className="h-4 w-4" /> Individual Plus
                </p>
                <div className="bg-green-50 rounded p-2 text-center">
                  <div className="font-semibold text-lg">{data.subscriptions.individual_plus}</div>
                  <div className="text-xs text-muted-foreground">Active members</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-red-500" />
              Churn Indicators
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <div>
                  <p className="font-medium text-red-700">Failed Invoices</p>
                  <p className="text-xs text-red-600">Payment issues requiring attention</p>
                </div>
                <div className="text-2xl font-bold text-red-700">{data.churn.failed_invoices}</div>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Canceled Subscriptions</p>
                  <p className="text-xs text-muted-foreground">Total all-time cancellations</p>
                </div>
                <div className="text-2xl font-bold">{data.churn.canceled_subscriptions}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// --- B) SUBSCRIPTIONS TAB ---
const SubscriptionsTab = ({ token }) => {
  const [data, setData] = useState({ subscriptions: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ sub_type: 'all', tier: 'all', status: 'all' });
  const [noteDialog, setNoteDialog] = useState({ open: false, userId: null });
  const [noteText, setNoteText] = useState('');
  const [noteFlag, setNoteFlag] = useState('none');
  
  useEffect(() => {
    fetchSubscriptions();
  }, [filters]);
  
  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.sub_type && filters.sub_type !== 'all') params.append('sub_type', filters.sub_type);
      if (filters.tier && filters.tier !== 'all') params.append('tier', filters.tier);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      
      const res = await fetch(`${API_URL}/api/admin/finance/subscriptions?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setData(await res.json());
      }
    } catch (error) {
      toast.error("Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  };
  
  const addNote = async () => {
    if (!noteText.trim()) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/finance/subscriptions/${noteDialog.userId}/note`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ note: noteText, flag: noteFlag && noteFlag !== 'none' ? noteFlag : null })
      });
      
      if (res.ok) {
        toast.success("Note added successfully");
        setNoteDialog({ open: false, userId: null });
        setNoteText('');
        setNoteFlag('');
        fetchSubscriptions();
      } else {
        throw new Error("Failed to add note");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      case 'past_due':
        return <Badge className="bg-red-100 text-red-700">Past Due</Badge>;
      case 'canceled':
        return <Badge variant="secondary">Canceled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4 flex-wrap">
            <Select value={filters.sub_type} onValueChange={(v) => setFilters(f => ({ ...f, sub_type: v }))}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="business">Business</SelectItem>
                <SelectItem value="individual">Individual</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.tier} onValueChange={(v) => setFilters(f => ({ ...f, tier: v }))}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="community_host">Community Host</SelectItem>
                <SelectItem value="local_featured">Local Featured</SelectItem>
                <SelectItem value="market_sponsor">Market Sponsor</SelectItem>
                <SelectItem value="plus">Plus</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="past_due">Past Due</SelectItem>
                <SelectItem value="canceled">Canceled</SelectItem>
              </SelectContent>
            </Select>
            
            <Badge variant="outline" className="h-10 px-3 flex items-center">
              Total: {data.total}
            </Badge>
          </div>
        </CardContent>
      </Card>
      
      {/* Subscriptions List */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="space-y-2">
          {data.subscriptions.map((sub) => (
            <Card key={sub.user_id} className={sub.admin_flag ? 'border-l-4 border-l-yellow-500' : ''}>
              <CardContent className="py-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    {/* WHO */}
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{sub.user_name || sub.business_name || 'Unknown'}</span>
                      {sub.username && <span className="text-muted-foreground">@{sub.username}</span>}
                      {sub.admin_flag && <Badge variant="warning" className="bg-yellow-100">{sub.admin_flag}</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">{sub.user_email}</p>
                    
                    {/* WHAT */}
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline">{sub.subscription_type}</Badge>
                      <Badge>{sub.tier}</Badge>
                      {getStatusBadge(sub.status)}
                      {sub.cancel_at_period_end && <Badge variant="destructive">Canceling</Badge>}
                    </div>
                    
                    {/* WHEN */}
                    <p className="text-xs text-muted-foreground mt-1">
                      Created: {new Date(sub.created_at).toLocaleDateString()} | 
                      Period ends: {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : 'N/A'}
                    </p>
                    
                    {/* WHERE */}
                    {sub.stripe_subscription_id && (
                      <p className="text-xs font-mono text-muted-foreground">
                        Stripe: {sub.stripe_subscription_id}
                      </p>
                    )}
                    
                    {/* Admin Notes */}
                    {sub.admin_notes?.length > 0 && (
                      <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                        <p className="font-medium text-xs mb-1">Notes:</p>
                        {sub.admin_notes.slice(-2).map((note, i) => (
                          <p key={i} className="text-xs text-muted-foreground">
                            {note.admin_name}: {note.note}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="text-right">
                    <div className="text-xl font-bold">${sub.amount.toFixed(2)}</div>
                    <p className="text-xs text-muted-foreground">/month</p>
                    <Dialog open={noteDialog.open && noteDialog.userId === sub.user_id} onOpenChange={(open) => setNoteDialog({ open, userId: open ? sub.user_id : null })}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" className="mt-2">
                          <Plus className="h-3 w-3 mr-1" /> Note
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add Note</DialogTitle>
                          <DialogDescription>Add an internal note to this subscription</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label>Note</Label>
                            <Textarea 
                              value={noteText} 
                              onChange={(e) => setNoteText(e.target.value)}
                              placeholder="Internal note..."
                            />
                          </div>
                          <div>
                            <Label>Flag (Optional)</Label>
                            <Select value={noteFlag} onValueChange={setNoteFlag}>
                              <SelectTrigger>
                                <SelectValue placeholder="No flag" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No flag</SelectItem>
                                <SelectItem value="review">Review</SelectItem>
                                <SelectItem value="fraud">Fraud</SelectItem>
                                <SelectItem value="vip">VIP</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button onClick={addNote}>Add Note</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {data.subscriptions.length === 0 && (
            <Alert><AlertDescription>No subscriptions found matching filters</AlertDescription></Alert>
          )}
        </div>
      )}
    </div>
  );
};

// --- C) SPONSORSHIP TAB ---
const SponsorshipTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  
  useEffect(() => {
    fetchSponsorship();
  }, [period]);
  
  const fetchSponsorship = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/finance/sponsorship?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setData(await res.json());
      }
    } catch (error) {
      toast.error("Failed to load sponsorship data");
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  
  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-4">
            <Label>Period</Label>
            <Input 
              type="month" 
              value={period} 
              onChange={(e) => setPeriod(e.target.value)}
              className="w-48"
            />
          </div>
        </CardContent>
      </Card>
      
      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Total Sponsorship Revenue</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  ${data.summary.total_sponsorship_revenue.toFixed(2)}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Creator Pool Allocated</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  ${data.summary.creator_pool_allocated.toFixed(2)}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Platform Retained ({(data.summary.platform_rate * 100).toFixed(0)}%)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  ${data.summary.platform_retained.toFixed(2)}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* By Business Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">By Business</CardTitle>
              <CardDescription>{data.total_entries} entries across {data.event_count} events</CardDescription>
            </CardHeader>
            <CardContent>
              {data.by_business.length > 0 ? (
                <div className="space-y-2">
                  {data.by_business.map((biz, i) => (
                    <div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium">{biz.business_name || biz.name}</p>
                        <p className="text-xs text-muted-foreground">{biz.user_id}</p>
                      </div>
                      <Badge variant="outline" className="text-lg">${biz.total_accrued.toFixed(2)}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">No sponsorship activity this period</p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// --- D) LEDGER TAB ---
const LedgerTab = ({ token }) => {
  const [data, setData] = useState({ entries: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [adjustDialog, setAdjustDialog] = useState({ open: false, entry: null });
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  
  useEffect(() => {
    fetchLedger();
  }, [statusFilter]);
  
  const fetchLedger = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('username', search);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      const res = await fetch(`${API_URL}/api/admin/finance/ledger?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setData(await res.json());
      }
    } catch (error) {
      toast.error("Failed to load ledger");
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    fetchLedger();
  };
  
  const submitAdjustment = async () => {
    if (!adjustAmount || !adjustReason) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/finance/ledger/${adjustDialog.entry.ledger_id}/adjust`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          adjustment_amount: parseFloat(adjustAmount),
          reason: adjustReason
        })
      });
      
      if (res.ok) {
        toast.success("Adjustment applied");
        setAdjustDialog({ open: false, entry: null });
        setAdjustAmount('');
        setAdjustReason('');
        fetchLedger();
      }
    } catch (error) {
      toast.error("Failed to apply adjustment");
    }
  };
  
  const exportCSV = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/finance/ledger/export?status=${statusFilter === 'all' ? '' : statusFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Convert to CSV
        const headers = Object.keys(data.data[0] || {}).join(',');
        const rows = data.data.map(row => Object.values(row).join(',')).join('\n');
        const csv = `${headers}\n${rows}`;
        
        // Download
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ledger_export_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        
        toast.success(`Exported ${data.total_rows} rows`);
      }
    } catch (error) {
      toast.error("Export failed");
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Search & Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4 flex-wrap">
            <form onSubmit={handleSearch} className="flex gap-2">
              <Input 
                placeholder="Search by username..." 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-48"
              />
              <Button type="submit" variant="outline" size="icon">
                <Search className="h-4 w-4" />
              </Button>
            </form>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="eligible">Eligible</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={exportCSV}>
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            
            <Badge variant="outline" className="h-10 px-3 flex items-center">
              Total: {data.total}
            </Badge>
          </div>
        </CardContent>
      </Card>
      
      {/* Ledger Entries */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="space-y-2">
          {data.entries.map((entry) => (
            <Card key={entry.ledger_id}>
              <CardContent className="py-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    {/* WHO */}
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{entry.business_name || entry.user_name}</span>
                      {entry.username && <span className="text-muted-foreground">@{entry.username}</span>}
                    </div>
                    
                    {/* WHAT */}
                    <div className="flex gap-2">
                      <Badge variant="outline">{entry.source_type}</Badge>
                      <Badge variant={entry.status === 'eligible' ? 'default' : 'secondary'}>{entry.status}</Badge>
                    </div>
                    
                    {/* WHEN */}
                    <p className="text-xs text-muted-foreground">
                      Period: {entry.period} | Created: {new Date(entry.created_at).toLocaleString()}
                    </p>
                    
                    {/* HOW - Calculation Breakdown */}
                    <div className="mt-2 p-2 bg-gray-50 rounded text-xs font-mono">
                      Base: ${entry.base_amount?.toFixed(2)} × (1 + {entry.participation_modifier?.toFixed(2)}) = ${entry.final_amount?.toFixed(2)}
                      {entry.participant_count && <span className="ml-2">({entry.participant_count} participants)</span>}
                    </div>
                    
                    {/* Threshold */}
                    <div className="flex items-center gap-2 text-xs">
                      <span>Running Balance: ${entry.running_balance?.toFixed(2)}</span>
                      {entry.threshold_met ? (
                        <CheckCircle className="h-3 w-3 text-green-500" />
                      ) : (
                        <span className="text-muted-foreground">(${(entry.threshold - entry.running_balance).toFixed(2)} to threshold)</span>
                      )}
                    </div>
                    
                    {/* Adjustments */}
                    {entry.adjustments?.length > 0 && (
                      <div className="mt-1 text-xs text-orange-600">
                        {entry.adjustments.length} adjustment(s) applied
                      </div>
                    )}
                  </div>
                  
                  <div className="text-right">
                    <div className="text-xl font-bold">${entry.final_amount?.toFixed(2)}</div>
                    <Dialog open={adjustDialog.open && adjustDialog.entry?.ledger_id === entry.ledger_id} onOpenChange={(open) => setAdjustDialog({ open, entry: open ? entry : null })}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" className="mt-2">
                          Adjust
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Manual Adjustment</DialogTitle>
                          <DialogDescription>Adjust ledger entry with reason</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label>Adjustment Amount</Label>
                            <Input 
                              type="number" 
                              step="0.01"
                              value={adjustAmount}
                              onChange={(e) => setAdjustAmount(e.target.value)}
                              placeholder="+5.00 or -2.50"
                            />
                          </div>
                          <div>
                            <Label>Reason (required)</Label>
                            <Textarea 
                              value={adjustReason}
                              onChange={(e) => setAdjustReason(e.target.value)}
                              placeholder="Why is this adjustment being made?"
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button onClick={submitAdjustment}>Apply Adjustment</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {data.entries.length === 0 && (
            <Alert><AlertDescription>No ledger entries found</AlertDescription></Alert>
          )}
        </div>
      )}
    </div>
  );
};

// --- E) PAYOUTS TAB ---
const PayoutsTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchPayouts();
  }, []);
  
  const fetchPayouts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/finance/payouts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setData(await res.json());
      }
    } catch (error) {
      toast.error("Failed to load payout data");
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  
  return (
    <div className="space-y-6">
      <Alert className="border-orange-200 bg-orange-50">
        <AlertTriangle className="h-4 w-4 text-orange-600" />
        <AlertDescription className="text-orange-800">
          {data?.message}
        </AlertDescription>
      </Alert>
      
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Eligible for Payout</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span>Users at Threshold (≥${data?.eligible_summary.threshold})</span>
                <span className="font-bold">{data?.eligible_summary.users_at_threshold}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Eligible Amount</span>
                <span className="font-bold text-green-600">${data?.eligible_summary.total_eligible_amount.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Stripe Connect Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-500" />
                <span>{data?.stripe_connect_status}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Stripe Connect must be configured before payouts can be processed.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Future Payout Fields (Ready)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(data?.payout_fields_ready || {}).map(([field, status]) => (
              <div key={field} className="flex items-center gap-2 p-2 bg-green-50 rounded">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-sm font-mono">{field}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// --- F) AUDIT LOG TAB ---
const AuditTab = ({ token }) => {
  const [data, setData] = useState({ entries: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  
  useEffect(() => {
    fetchAuditLog();
  }, [eventTypeFilter]);
  
  const fetchAuditLog = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (eventTypeFilter && eventTypeFilter !== 'all') params.append('event_type', eventTypeFilter);
      
      const res = await fetch(`${API_URL}/api/admin/finance/audit?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setData(await res.json());
      }
    } catch (error) {
      toast.error("Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4">
            <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Event Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                {data.event_types?.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Badge variant="outline" className="h-10 px-3 flex items-center">
              Total: {data.total}
            </Badge>
          </div>
        </CardContent>
      </Card>
      
      {/* Audit Entries */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="space-y-2">
          {data.entries.map((entry) => (
            <Card key={entry.audit_id}>
              <CardContent className="py-3">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{entry.event_type}</Badge>
                      {entry.target_type && <Badge variant="secondary">{entry.target_type}</Badge>}
                    </div>
                    
                    <p className="text-sm">
                      <span className="font-medium">Admin:</span> {entry.admin_name || 'System'}
                    </p>
                    
                    {entry.reason && (
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium">Reason:</span> {entry.reason}
                      </p>
                    )}
                    
                    {entry.amount && (
                      <p className="text-sm">
                        <span className="font-medium">Amount:</span> ${entry.amount.toFixed(2)}
                      </p>
                    )}
                    
                    {entry.stripe_event_id && (
                      <p className="text-xs font-mono text-muted-foreground">
                        Stripe: {entry.stripe_event_id}
                      </p>
                    )}
                  </div>
                  
                  <div className="text-right text-xs text-muted-foreground">
                    {new Date(entry.created_at).toLocaleString()}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {data.entries.length === 0 && (
            <Alert><AlertDescription>No audit entries found</AlertDescription></Alert>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminFinancePortal;
